import { useCallback } from "react";

import { convertToAssetId, trpc, useMoveAssetsToFolder } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { AssetTypes, getBuildAssetUrl, useNotification } from "@zapier/utils";

interface UseCreateAssetInFolderOptions {
  folderId: string;
}

export function useCreateAssetInFolder({
  folderId,
}: UseCreateAssetInFolderOptions) {
  const t = useTranslations("folder-creation-button");
  const { createNotification } = useNotification();
  const utils = trpc.useUtils();

  const { moveAssetsToFolder, isPending: isMoving } = useMoveAssetsToFolder({
    folderId,
  });

  // Table creation
  const { mutate: createTableMutation, isPending: isCreatingTable } =
    trpc.table.create.useMutation();

  // Interface/Form creation
  const { mutate: createInterfaceMutation, isPending: isCreatingInterface } =
    trpc.interface.create.useMutation();

  // Chatbot creation
  const { mutate: createChatbotMutation, isPending: isCreatingChatbot } =
    trpc.chatbot.create.useMutation();

  // Canvas creation
  const { mutate: createCanvasMutation, isPending: isCreatingCanvas } =
    trpc.canvas.create.useMutation();

  // Agent creation
  const { mutate: createAgentMutation, isPending: isCreatingAgent } =
    trpc.agent.create.useMutation();

  const invalidateQueries = useCallback(() => {
    utils.asset.getHydratedFolderAssets.invalidate({ folderId });
    utils.folder.foldersLookup.invalidate();
  }, [folderId, utils]);

  const navigateToAsset = useCallback(
    (assetType: AssetTypes, assetId: string) => {
      const url = getBuildAssetUrl(assetType, assetId);
      if (url) {
        window.open(url, "_blank");
      }
    },
    [],
  );

  const moveAndNavigate = useCallback(
    (assetType: AssetTypes, assetId: string, assetName: string) => {
      const fullAssetId = convertToAssetId(assetType, assetId);

      moveAssetsToFolder(
        {
          assetIds: [fullAssetId],
          destinationFolderId: folderId,
        },
        {
          onSuccess: () => {
            invalidateQueries();
            navigateToAsset(assetType, assetId);
          },
          onError: () => {
            // Even if move fails, navigate to the asset
            // User can move it later
            createNotification({
              message: t("notifications.move-to-folder-error", {
                assetName,
              }),
              type: "warning",
            });
            navigateToAsset(assetType, assetId);
          },
        },
      );
    },
    [
      createNotification,
      folderId,
      invalidateQueries,
      moveAssetsToFolder,
      navigateToAsset,
      t,
    ],
  );

  const createTable = useCallback(() => {
    const defaultName = t("default-names.table");
    createTableMutation(
      { name: defaultName },
      {
        onSuccess: (response) => {
          const tableId = response?.data?.id;
          if (tableId) {
            moveAndNavigate(AssetTypes.Table, tableId, defaultName);
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.create-error", {
              assetType: t("menu-items.table"),
            }),
            type: "error",
          });
        },
      },
    );
  }, [createNotification, createTableMutation, moveAndNavigate, t]);

  const createInterface = useCallback(() => {
    const defaultName = t("default-names.interface");
    createInterfaceMutation(
      { name: defaultName, createHomePage: true },
      {
        onSuccess: (response) => {
          const interfaceId = response?.id;
          if (interfaceId) {
            moveAndNavigate(AssetTypes.Interface, interfaceId, defaultName);
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.create-error", {
              assetType: t("menu-items.interface"),
            }),
            type: "error",
          });
        },
      },
    );
  }, [createInterfaceMutation, createNotification, moveAndNavigate, t]);

  const createChatbot = useCallback(() => {
    const defaultName = t("default-names.chatbot");
    createChatbotMutation(
      { chatbotName: defaultName },
      {
        onSuccess: (response) => {
          const chatbotId = response?.id;
          if (chatbotId) {
            moveAndNavigate(AssetTypes.Chatbot, chatbotId, defaultName);
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.create-error", {
              assetType: t("menu-items.chatbot"),
            }),
            type: "error",
          });
        },
      },
    );
  }, [createChatbotMutation, createNotification, moveAndNavigate, t]);

  const createCanvas = useCallback(() => {
    const defaultName = t("default-names.canvas");
    createCanvasMutation(
      { title: "" },
      {
        onSuccess: (canvas) => {
          const canvasId = canvas?.id;
          if (canvasId) {
            moveAndNavigate(AssetTypes.Canvas, canvasId, defaultName);
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.create-error", {
              assetType: t("menu-items.canvas"),
            }),
            type: "error",
          });
        },
      },
    );
  }, [createCanvasMutation, createNotification, moveAndNavigate, t]);

  const createAgent = useCallback(() => {
    const defaultName = t("default-names.agent");
    createAgentMutation(
      { name: defaultName },
      {
        onSuccess: (response) => {
          const agentId = response?.id;
          if (agentId) {
            moveAndNavigate(AssetTypes.Agent, agentId, defaultName);
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.create-error", {
              assetType: t("menu-items.agent"),
            }),
            type: "error",
          });
        },
      },
    );
  }, [createAgentMutation, createNotification, moveAndNavigate, t]);

  const isCreating =
    isCreatingTable ||
    isCreatingInterface ||
    isCreatingChatbot ||
    isCreatingCanvas ||
    isCreatingAgent ||
    isMoving;

  return {
    createTable,
    createInterface,
    createChatbot,
    createCanvas,
    createAgent,
    isCreating,
    isCreatingTable,
    isCreatingInterface,
    isCreatingChatbot,
    isCreatingCanvas,
    isCreatingAgent,
  };
}
